import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { QrCodeRecord, StickerSheetAssignmentChange, StickerSizeInches } from './app.types';

@Component({
  selector: 'app-sticker-printer',
  standalone: false,
  template: `
    <section class="sticker-printer">
      <ion-list lines="full">
        <ion-item>
          <ion-input
            type="number"
            inputmode="decimal"
            [ngModel]="stickerSize"
            (ngModelChange)="stickerSizeChange.emit($event)"
            label="Sticker Size Inches"
            labelPlacement="stacked"
            aria-label="Sticker size inches"
            placeholder="1"
            min="0.5"
            max="2"
            step="0.05"
          ></ion-input>
        </ion-item>
      </ion-list>

      <ion-note color="medium">
        Select one or more sticker positions, then search for the QR code to place there.
      </ion-note>

      <div class="sticker-layout">
        <div class="sticker-position-actions">
          <ion-button type="button" size="small" fill="outline" (click)="selectActiveRow()">Select Row</ion-button>
          <ion-button type="button" size="small" fill="outline" (click)="selectActiveColumn()">Select Column</ion-button>
          <ion-button type="button" size="small" fill="clear" (click)="clearPositionSelection()">One Position</ion-button>
        </div>

        <div
          class="sticker-layout-grid"
          [style.grid-template-columns]="'repeat(' + layoutColumns + ', minmax(32px, 1fr))'"
        >
          <button
            type="button"
            class="sticker-layout-cell"
            [class.active]="positionSelected(positionIndex)"
            [class.assigned]="assignedQrCodeId(positionIndex) !== null"
            *ngFor="let positionIndex of positionIndexes()"
            (click)="togglePosition(positionIndex)"
          >
            <span>{{ positionIndex + 1 }}</span>
            <small *ngIf="assignedQrCodeId(positionIndex) !== null">{{ assignedQrCodeTitle(positionIndex) }}</small>
          </button>
        </div>

        <div class="sticker-assignment-search">
          <label class="sticker-assignment-label" for="sticker-assignment-search">
            {{ selectedPositionLabel() }}
          </label>
          <input
            id="sticker-assignment-search"
            class="sticker-assignment-input"
            type="search"
            [value]="assignmentSearchQuery"
            placeholder="Search QR codes"
            (input)="updateAssignmentSearch($event)"
          >

          <ion-note color="medium" *ngIf="isLoading">
            <ion-spinner name="crescent"></ion-spinner>
            Loading QR codes...
          </ion-note>
          <ion-note color="medium" *ngIf="!isLoading && qrCodes.length === 0">No saved QR codes match this search.</ion-note>

          <div class="sticker-assignment-results" *ngIf="qrCodes.length > 0">
            <button
              type="button"
              class="sticker-assignment-result"
              [disabled]="!hasSelectedPositions()"
              (click)="assignSelectedPositions(null)"
            >
              Blank
            </button>
            <button
              type="button"
              class="sticker-assignment-result"
              [class.active]="selectedPositionsQrCodeId() === qrCode.id"
              [disabled]="!hasSelectedPositions()"
              *ngFor="let qrCode of visibleQrCodes()"
              (click)="assignSelectedPositions(qrCode.id)"
            >
              <span>{{ qrCode.title || qrCode.redirect_path }}</span>
              <small>{{ qrCode.target_url }}</small>
            </button>
          </div>

          <div class="sticker-assignment-pagination" *ngIf="totalPages > 1">
            <ion-button type="button" size="small" fill="outline" [disabled]="isLoading || page <= 1" (click)="pageChange.emit(page - 1)">
              Previous
            </ion-button>
            <span>Page {{ page }} of {{ totalPages }}</span>
            <ion-button type="button" size="small" fill="outline" [disabled]="isLoading || page >= totalPages" (click)="pageChange.emit(page + 1)">
              Next
            </ion-button>
          </div>
        </div>
      </div>

      <ion-button type="button" fill="outline" [disabled]="assignmentCount === 0" (click)="print.emit()">
        Print Sticker Sheet
      </ion-button>
    </section>
  `,
  styles: [`
    .sticker-printer {
      display: grid;
      gap: 12px;
      margin-top: 20px;
    }

    .sticker-printer > ion-list {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 8px;
      background: transparent;
    }

    .sticker-printer ion-item {
      --padding-start: 0;
      --inner-padding-end: 0;
    }

    .sticker-printer ion-button {
      justify-self: start;
    }

    .sticker-layout {
      display: grid;
      gap: 10px;
    }

    .sticker-position-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .sticker-layout-grid {
      display: grid;
      gap: 4px;
      max-width: 420px;
    }

    .sticker-layout-cell {
      aspect-ratio: 1;
      border: 1px solid var(--ion-color-medium);
      border-radius: 999px;
      background: var(--ion-color-light);
      color: var(--ion-color-dark);
      display: grid;
      place-items: center;
      min-width: 0;
      padding: 4px;
      text-align: center;
    }

    .sticker-layout-cell.active {
      border-color: var(--ion-color-primary);
      box-shadow: 0 0 0 2px rgba(var(--ion-color-primary-rgb), 0.22);
    }

    .sticker-layout-cell.assigned {
      background: rgba(var(--ion-color-primary-rgb), 0.12);
    }

    .sticker-layout-cell small {
      display: block;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 0.62rem;
    }

    .sticker-assignment-search {
      display: grid;
      gap: 8px;
      max-width: 520px;
    }

    .sticker-assignment-label {
      color: var(--ion-color-medium);
      font-size: 0.82rem;
    }

    .sticker-assignment-input {
      width: 100%;
      min-height: 42px;
      border: 1px solid var(--ion-color-medium);
      border-radius: 6px;
      padding: 8px 10px;
      background: var(--ion-background-color);
      color: var(--ion-text-color);
    }

    .sticker-assignment-results {
      display: grid;
      gap: 6px;
      max-height: 240px;
      overflow: auto;
    }

    .sticker-assignment-result {
      border: 1px solid var(--ion-color-light-shade);
      border-radius: 6px;
      background: var(--ion-color-light);
      color: var(--ion-color-dark);
      display: grid;
      gap: 2px;
      padding: 8px 10px;
      text-align: left;
    }

    .sticker-assignment-result.active {
      border-color: var(--ion-color-primary);
      box-shadow: 0 0 0 2px rgba(var(--ion-color-primary-rgb), 0.16);
    }

    .sticker-assignment-result:disabled {
      opacity: 0.55;
    }

    .sticker-assignment-result small {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: var(--ion-color-medium);
    }

    .sticker-assignment-search ion-note {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .sticker-assignment-search ion-spinner {
      width: 16px;
      height: 16px;
    }

    .sticker-assignment-pagination {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      color: var(--ion-color-medium);
      font-size: 0.82rem;
    }
  `]
})
export class StickerPrinterComponent implements OnChanges {
  @Input() stickerSize: StickerSizeInches = 1;
  @Input() layoutColumns = 1;
  @Input() layoutRows = 1;
  @Input() assignmentCount = 0;
  @Input() qrCodes: QrCodeRecord[] = [];
  @Input() assignments: Record<number, number> = {};
  @Input() assignedQrCodes: Record<number, QrCodeRecord> = {};
  @Input() searchQuery = '';
  @Input() isLoading = false;
  @Input() page = 1;
  @Input() totalPages = 1;

  @Output() stickerSizeChange = new EventEmitter<StickerSizeInches | string>();
  @Output() assignmentChange = new EventEmitter<StickerSheetAssignmentChange>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() print = new EventEmitter<void>();

  activePositionIndex = 0;
  selectedPositionIndexes: number[] = [0];
  assignmentSearchQuery = '';

  ngOnChanges(): void {
    this.activePositionIndex = Math.min(this.activePositionIndex, Math.max(0, this.layoutColumns * this.layoutRows - 1));
    this.selectedPositionIndexes = this.selectedPositionIndexes
      .filter((index) => index < this.layoutColumns * this.layoutRows);
    if (this.assignmentSearchQuery !== this.searchQuery) {
      this.assignmentSearchQuery = this.searchQuery;
    }
  }

  positionIndexes(): number[] {
    return Array.from({ length: this.layoutColumns * this.layoutRows }, (_, index) => index);
  }

  selectedPositionsQrCodeId(): number | null {
    if (!this.hasSelectedPositions()) {
      return null;
    }

    const firstQrCodeId = this.assignedQrCodeId(this.selectedPositionIndexes[0]);
    return this.selectedPositionIndexes.every((index) => this.assignedQrCodeId(index) === firstQrCodeId) ? firstQrCodeId : null;
  }

  assignedQrCodeId(index: number): number | null {
    return this.assignments[index] ?? null;
  }

  assignedQrCodeTitle(index: number): string {
    const qrCodeId = this.assignedQrCodeId(index);
    const qrCode = this.assignedQrCodes[index] || this.qrCodes.find((record) => record.id === qrCodeId);
    return qrCode?.title || qrCode?.redirect_path || '';
  }

  positionSelected(index: number): boolean {
    return this.selectedPositionIndexes.includes(index);
  }

  hasSelectedPositions(): boolean {
    return this.selectedPositionIndexes.length > 0;
  }

  togglePosition(index: number): void {
    this.activePositionIndex = index;

    if (this.positionSelected(index)) {
      this.selectedPositionIndexes = this.selectedPositionIndexes.length === 1
        ? [index]
        : this.selectedPositionIndexes.filter((positionIndex) => positionIndex !== index);
      return;
    }

    this.selectedPositionIndexes = [...this.selectedPositionIndexes, index].sort((first, second) => first - second);
  }

  selectActiveRow(): void {
    const row = Math.floor(this.activePositionIndex / this.layoutColumns);
    this.selectedPositionIndexes = Array.from({ length: this.layoutColumns }, (_, column) => row * this.layoutColumns + column);
  }

  selectActiveColumn(): void {
    const column = this.activePositionIndex % this.layoutColumns;
    this.selectedPositionIndexes = Array.from({ length: this.layoutRows }, (_, row) => row * this.layoutColumns + column);
  }

  clearPositionSelection(): void {
    this.selectedPositionIndexes = [this.activePositionIndex];
  }

  selectedPositionLabel(): string {
    if (!this.hasSelectedPositions()) {
      return 'No sticker positions selected';
    }

    if (this.selectedPositionIndexes.length === 1) {
      return `Position ${this.selectedPositionIndexes[0] + 1}`;
    }

    return `${this.selectedPositionIndexes.length} positions selected`;
  }

  visibleQrCodes(): QrCodeRecord[] {
    return this.qrCodes;
  }

  updateAssignmentSearch(event: Event): void {
    this.assignmentSearchQuery = (event.target as HTMLInputElement).value;
    this.searchChange.emit(this.assignmentSearchQuery);
  }

  assignSelectedPositions(qrCodeId: number | null): void {
    if (!this.hasSelectedPositions()) {
      return;
    }

    const assignedPositionIndexes = [...this.selectedPositionIndexes];
    const qrCode = qrCodeId === null ? undefined : this.qrCodes.find((record) => record.id === qrCodeId);
    this.assignmentChange.emit({
      indexes: assignedPositionIndexes,
      qrCodeId,
      qrCode
    });

    this.activePositionIndex = assignedPositionIndexes[0] ?? this.activePositionIndex;
    this.selectedPositionIndexes = [];
  }
}
